import { getSession } from '../api/supabase';

// Unified monitors service. Replaces both anomalyService.js and
// metricsService.js (post-2026-05-18 monitors unification — see
// sot_docs/MONITORS_REFACTOR.md). The backend exposes a single
// /api/connections/<id>/monitors/* surface for what used to be
// /api/connections/<id>/anomalies/* + /api/metrics/custom/*.
//
// Source-type / evaluation-mode / severity vocabularies match the
// CHECK constraints on the monitors / monitor_alerts tables. Frontend
// fetches the canonical lists via /api/monitors/metadata so dropdowns
// can't drift from the backend.

const API_BASE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:5000/api'
    : process.env.REACT_APP_API_BASE_URL ||
      'https://sparvi-webapp-fjdjdvh2bse9d0gm.centralus-01.azurewebsites.net/api';

class MonitorsService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // ------------------------------------------------------------------
  // Authenticated request helper. Mirrors anomalyService.js — keep
  // these aligned so existing telemetry / error-handling patterns
  // apply to monitors without re-instrumentation.
  // ------------------------------------------------------------------
  async makeRequest(endpoint, options = {}) {
    try {
      const session = await getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Authentication required');

      const url = `${this.baseURL}${endpoint}`;
      const response = await fetch(url, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText || `HTTP ${response.status}` };
        }
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }

      // 204 No Content (we don't return them today, but defensive)
      if (response.status === 204) return null;
      return await response.json();
    } catch (error) {
      console.error(`MonitorsService: ${endpoint} →`, error);
      throw error;
    }
  }

  // ------------------------------------------------------------------
  // Metadata (enum dropdowns)
  // ------------------------------------------------------------------
  // Returns { source_types, builtin_table_metrics,
  // builtin_column_metrics, evaluation_modes, statistical_methods,
  // threshold_directions, severities }. Backend reads these from
  // core/monitors/models.py so the FE and BE can't drift.
  async getMetadata() {
    return await this.makeRequest('/monitors/metadata');
  }

  // ------------------------------------------------------------------
  // Monitor CRUD
  // ------------------------------------------------------------------
  async listMonitors(connectionId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters.evaluation_mode) params.append('evaluation_mode', filters.evaluation_mode);
    if (filters.source_type) params.append('source_type', filters.source_type);

    const qs = params.toString();
    const endpoint = `/connections/${connectionId}/monitors${qs ? `?${qs}` : ''}`;
    const response = await this.makeRequest(endpoint);
    return response.monitors || [];
  }

  async getMonitor(connectionId, monitorId) {
    const response = await this.makeRequest(
      `/connections/${connectionId}/monitors/${monitorId}`
    );
    return response.monitor;
  }

  async createMonitor(connectionId, payload) {
    const response = await this.makeRequest(
      `/connections/${connectionId}/monitors`,
      { method: 'POST', body: JSON.stringify(payload) }
    );
    return response.monitor;
  }

  // Partial update — only include fields you want to change. The backend
  // re-validates the full evaluation_config / source_config when those
  // change, so partial threshold tweaks still catch cross-field rule
  // violations (e.g., critical > warning for direction='below').
  async updateMonitor(connectionId, monitorId, payload) {
    const response = await this.makeRequest(
      `/connections/${connectionId}/monitors/${monitorId}`,
      { method: 'PUT', body: JSON.stringify(payload) }
    );
    return response.monitor;
  }

  async deleteMonitor(connectionId, monitorId) {
    await this.makeRequest(
      `/connections/${connectionId}/monitors/${monitorId}`,
      { method: 'DELETE' }
    );
    return true;
  }

  // ------------------------------------------------------------------
  // Operations
  // ------------------------------------------------------------------

  // Manual synchronous trigger. Returns MonitorRunResult:
  // { run_id, status, values_recorded, alerts_fired, duration_ms, error }
  async runMonitor(connectionId, monitorId) {
    return await this.makeRequest(
      `/connections/${connectionId}/monitors/${monitorId}/run`,
      { method: 'POST' }
    );
  }

  // Historical values for a monitor — used for sparkline + history modal.
  // Filters: { limit, segment_value }. For segmented monitors, pass
  // segment_value to get one segment's series; omit for all.
  async getMonitorValues(connectionId, monitorId, options = {}) {
    const { limit = 200, segment_value } = options;
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (segment_value !== undefined && segment_value !== null) {
      params.append('segment_value', segment_value);
    }
    const response = await this.makeRequest(
      `/connections/${connectionId}/monitors/${monitorId}/values?${params.toString()}`
    );
    return response.values || [];
  }

  async getMonitorAlerts(connectionId, monitorId, options = {}) {
    const { status, limit = 100 } = options;
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (status) params.append('status', status);
    const response = await this.makeRequest(
      `/connections/${connectionId}/monitors/${monitorId}/alerts?${params.toString()}`
    );
    return response.alerts || [];
  }

  // ------------------------------------------------------------------
  // Connection-wide aggregates
  // ------------------------------------------------------------------
  async listConnectionAlerts(connectionId, options = {}) {
    const { status, severity, limit = 50 } = options;
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (status) params.append('status', status);
    if (severity) params.append('severity', severity);
    const response = await this.makeRequest(
      `/connections/${connectionId}/monitor-alerts?${params.toString()}`
    );
    return response.alerts || [];
  }

  async updateAlertStatus(connectionId, alertId, status, resolutionNote = null) {
    const body = { status };
    if (resolutionNote !== null) body.resolution_note = resolutionNote;
    const response = await this.makeRequest(
      `/connections/${connectionId}/monitor-alerts/${alertId}`,
      { method: 'PUT', body: JSON.stringify(body) }
    );
    return response.alert;
  }

  async listConnectionRuns(connectionId, options = {}) {
    const { monitor_id, status, limit = 50 } = options;
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (monitor_id) params.append('monitor_id', monitor_id);
    if (status) params.append('status', status);
    const response = await this.makeRequest(
      `/connections/${connectionId}/monitor-runs?${params.toString()}`
    );
    return response.runs || [];
  }

  // ------------------------------------------------------------------
  // Display helpers — used by the Monitors page card grid + alerts list.
  // Same shape as anomalyService had for cross-component reuse.
  // ------------------------------------------------------------------

  // Display formatting for monitor values. value comes back as a string
  // from PostgREST (NUMERIC → string serialization to preserve precision);
  // coerce here so callers don't need to remember.
  //
  // Use this for unconstrained surfaces (chart tooltips, detail modal
  // hero numbers). For card / list / table rows use formatCompact()
  // so multi-million-dollar sums don't overflow the container.
  formatValue(value, resultType = 'numeric') {
    if (value === null || value === undefined) return 'N/A';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    if (resultType === 'percentage') return `${(num * 100).toFixed(2)}%`;
    if (num === Math.floor(num)) return num.toLocaleString();
    return num.toFixed(2);
  }

  // Compact formatting for constrained surfaces (monitor cards, segment
  // list rows). Abbreviates with K/M/B/T at >=1000 — the same convention
  // Stripe / Datadog / Grafana use for metric displays. Up to 2 decimals
  // so "1.50M" rather than "1.5M" for visual consistency. Callers should
  // pair this with a `title={formatValue(value)}` attribute so the full
  // precision is one hover away — the abbreviation alone hides digits
  // the user might care about.
  formatCompact(value, resultType = 'numeric') {
    if (value === null || value === undefined) return 'N/A';
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    // Percentages cap at +/-100 in practice — never overflow a card,
    // so we don't abbreviate them. Falls through to the percentage
    // branch of formatValue.
    if (resultType === 'percentage') return `${(num * 100).toFixed(2)}%`;
    const abs = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(2)}K`;
    // <1000 fits comfortably in any card-sized container at 3xl font,
    // so show locale-formatted full digits.
    return num === Math.floor(num) ? num.toLocaleString() : num.toFixed(2);
  }

  // Unified severity vocabulary — locked by monitor_alerts.severity CHECK.
  // Returns a Tailwind text color class.
  getSeverityColor(severity) {
    switch (severity) {
      case 'critical':
        return 'text-red-700';
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-400';
    }
  }

  // Tailwind badge style for severity (background + text).
  getSeverityBadge(severity) {
    switch (severity) {
      case 'critical':
        return 'bg-red-200 text-red-900';
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusBadge(status) {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'acknowledged':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  // Human-friendly label for alert_type — used in the alerts list.
  alertTypeLabel(alertType) {
    if (alertType === 'statistical_anomaly') return 'Anomaly';
    if (alertType === 'threshold_breach') return 'Threshold';
    return alertType || 'Alert';
  }

  // Collapse multiple per-segment rows at the same calculated_at into a
  // single aggregate point. Used for the collapsed-card sparkline +
  // history modal on segmented monitors — without this, a 3-segment
  // monitor that ran 5 times plots 15 zig-zag points (one per
  // monitor_values row) instead of 5 trend points.
  //
  // For non-segmented monitors this is a no-op since there's only one
  // row per timestamp, and the per-segment expand UX on the card uses
  // the raw rows directly.
  //
  // method='avg' is the default because it's the most generic
  // visualization across all metric types — sum would dominate for
  // row_count but exceed 100% for percentages; max would highlight
  // outliers; avg gives a stable "typical value" that reads cleanly
  // regardless of metric. Callers can override if they have a strong
  // signal about the source metric (e.g., a future "show total" toggle).
  aggregateValuesByTimestamp(values, method = 'avg') {
    if (!values || values.length === 0) return [];
    const buckets = new Map();
    for (const v of values) {
      const ts = v.calculated_at;
      if (!ts) continue;
      const num = typeof v.value === 'number' ? v.value : Number(v.value);
      if (!Number.isFinite(num)) continue;
      if (!buckets.has(ts)) buckets.set(ts, []);
      buckets.get(ts).push(num);
    }
    const out = [];
    for (const [ts, vals] of buckets.entries()) {
      let agg;
      if (method === 'sum') agg = vals.reduce((a, b) => a + b, 0);
      else if (method === 'max') agg = Math.max(...vals);
      else if (method === 'min') agg = Math.min(...vals);
      else agg = vals.reduce((a, b) => a + b, 0) / vals.length;
      out.push({
        calculated_at: ts,
        value: agg,
        // Marker so debugging is easier — also lets the card label the
        // aggregated value as "avg across segments" if it wants to.
        _aggregated: vals.length > 1,
        _segment_count: vals.length,
      });
    }
    // Preserve the backend's DESC-by-calculated_at convention so the
    // existing reverse() in MonitorSparkline still flips us to ASC.
    out.sort((a, b) => (a.calculated_at < b.calculated_at ? 1 : -1));
    return out;
  }

  // Short human-readable description of a monitor's schedule. Used on
  // the card + detail modal so the user can see cadence at a glance
  // without opening the form. Falls back to the legacy minutes field
  // if the monitor row predates the schedule_type/schedule_config
  // columns and from_row hasn't been hit.
  scheduleSummary(monitor) {
    if (!monitor) return '';
    const type = monitor.schedule_type;
    const cfg = monitor.schedule_config || {};
    if (type === 'manual') return 'Manual only — no schedule';
    if (type === 'interval') {
      const every = cfg.every || monitor.collection_interval_minutes || 60;
      const unit = cfg.unit || 'minutes';
      const unitLabel = unit === 'hours'
        ? (every === 1 ? 'hour' : 'hours')
        : (every === 1 ? 'minute' : 'minutes');
      return `Every ${every} ${unitLabel}`;
    }
    if (type === 'daily') {
      return `Daily at ${this._formatHM(cfg.hour, cfg.minute)} ${cfg.timezone || 'UTC'}`;
    }
    if (type === 'weekly') {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayLabel = days[cfg.day_of_week] || 'day';
      return `Weekly on ${dayLabel} at ${this._formatHM(cfg.hour, cfg.minute)} ${cfg.timezone || 'UTC'}`;
    }
    // Unknown / legacy — fall back to the minutes field.
    const m = monitor.collection_interval_minutes;
    return m ? `Every ${m} minutes` : '';
  }

  _formatHM(hour, minute) {
    const h = (hour ?? 0).toString().padStart(2, '0');
    const m = (minute ?? 0).toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  // Human-friendly label for source_type — used in the create form picker.
  sourceTypeLabel(sourceType) {
    switch (sourceType) {
      case 'builtin_table_metric':
        return 'Built-in table metric';
      case 'builtin_column_metric':
        return 'Built-in column metric';
      case 'custom_sql':
        return 'Custom SQL';
      default:
        return sourceType || 'Source';
    }
  }
}

const monitorsService = new MonitorsService();
export default monitorsService;
